import React, { FC } from "react";
import { CheckCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Upload,
} from "antd";
import { EyeOutlined, UploadOutlined } from "@ant-design/icons";
import { useUserFunctionalities } from "../../../../utils/UserFunctionalities";
import {
  HERITAGE_ADMISSION_TYPES,
  HERITAGE_DATE_FORMAT,
  HERITAGE_ROOM_CATEGORIES,
} from "../../../../apis/SchemeService/HERITAGE/heritageService";

type PreAuthHeritageProps = {
  form: any;
  handleSaveConfirmationModal: (
    state: "save" | "update" | "intimate" | "approve" | "initiate" | "submit"
  ) => void;
  handleSubmit?: () => void;
  isSaving: boolean;
  isIntimating: boolean;
  isSubmitting?: boolean;
  integratedWithPortal?: boolean;
  isApproving: boolean;
  isApproved: boolean;
  isSubmitted: boolean;
  isInitiated: boolean;
  isIntimated: boolean;
  hasExistingRecord: boolean;
  documentRequestResponseDocuments: any;
  documentUrls: any[];
  handleFileChange: (documentType: string, isMultiple: boolean) => (e: any) => void;
  handleOpen: (docType: any, documentUrl: any) => void;
  uploadFileList: any[];
  showVerifyButton?: boolean;
  isValidationLoading?: boolean;
  registrationValidationStatus?: any;
  handleClaimValidation?: () => void;
};

const readonlyFieldStyle: React.CSSProperties = {
  backgroundColor: "#f0f0f0",
  color: "#595959",
};

const sectionTitleStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 15,
  marginBottom: 12,
  marginTop: 8,
  color: "#004c8f",
};

const requiredRule = (message: string) => ({ required: true, message });

const mobileTenDigitRules = [
  requiredRule("Please enter mobile number"),
  { pattern: /^\d{10}$/, message: "Mobile number must be exactly 10 digits" },
];

const decimalAmountMessage =
  "Only numeric values with up to 2 decimal places are allowed!";

const decimalAmountRule = {
  pattern: /^(\d+(\.\d{1,2})?|\.\d{1,2})$/,
  message: decimalAmountMessage,
};

const optionalDecimalRules = [
  {
    validator: (_: unknown, value: string) => {
      if (!value) return Promise.resolve();
      if (!/^(\d+(\.\d{1,2})?|\.\d{1,2})$/.test(value)) {
        return Promise.reject(new Error(decimalAmountMessage));
      }
      return Promise.resolve();
    },
  },
];

const wholeNumberRule = {
  pattern: /^\d+$/,
  message: "Only whole numbers are allowed",
};

const optionalWholeNumberRules = [
  {
    validator: (_: unknown, value: string) => {
      if (!value) return Promise.resolve();
      if (!/^\d{1,3}$/.test(value)) {
        return Promise.reject(new Error("Days should be upto 3 digits only"));
      }
      return Promise.resolve();
    },
  },
];

const noOfDaysRules = [
  requiredRule("Please enter number of days"),
  {
    pattern: /^\d{1,3}$/,
    message: "Days should be upto 3 digits only",
  },
  {
    validator: (_: unknown, value: string) => {
      if (!value) return Promise.resolve();
      if (Number(value) < 1) {
        return Promise.reject(new Error("Number of days must be at least 1"));
      }
      return Promise.resolve();
    },
  },
];

const PreAuthHeritage: FC<PreAuthHeritageProps> = ({
  form,
  handleSaveConfirmationModal,
  isSaving,
  isIntimating,
  handleSubmit,
  isSubmitting = false,
  integratedWithPortal = true,
  isApproving,
  isApproved,
  isSubmitted,
  isInitiated,
  isIntimated,
  hasExistingRecord,
  documentRequestResponseDocuments,
  documentUrls,
  handleFileChange,
  handleOpen,
  uploadFileList,
  showVerifyButton,
  isValidationLoading,
  registrationValidationStatus,
  handleClaimValidation,
}) => {
  const { hasFunctionality } = useUserFunctionalities();
  const actionButtonStyle = {
    backgroundColor: "rgb(0, 76, 143)",
    color: "#fff",
    boxShadow: "0px 2px 8px 0px #00000040",
    width: "100%",
  };

  const renderDocumentRows = (type: "requestedDocuments" | "responseDocuments") => {
    const docs = documentRequestResponseDocuments?.[type] || [];
    return docs?.map((docType: any) => {
      const matchingDocuments = documentUrls?.filter(
        (doc) => doc?.documentLabelType === docType?.label
      );

      return (
        <div
          key={`${type}-${docType?.id}`}
          style={{
            border: "1px solid #eee",
            borderRadius: 8,
            padding: "8px 10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Upload
              multiple={Boolean(docType?.multiple)}
              showUploadList={false}
              beforeUpload={() => false}
              {...(docType?.multiple ? { fileList: uploadFileList } : {})}
              onChange={handleFileChange(docType?.labelId, Boolean(docType?.multiple))}
              disabled={isSubmitted}
            >
              <Button icon={<UploadOutlined />} disabled={isSubmitted} />
            </Upload>
            <span>
              {docType?.label}
              {docType?.required ? <span style={{ color: "red" }}> *</span> : null}
            </span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {matchingDocuments?.map((doc: any, idx: number) => (
              <Button
                key={`${doc?.id}-${idx}`}
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleOpen(doc?.documentLabelType, doc?.documentUrl)}
              >
                {idx + 1}
              </Button>
            ))}
          </div>
        </div>
      );
    });
  };

  return (
    <div style={{ margin: "0px 0.5rem" }}>
      <Form form={form} layout="vertical" disabled={isSubmitted}>
        <div style={sectionTitleStyle}>Member Detail</div>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Patient Name"
              name="patientName"
              rules={[requiredRule("Please enter patient name")]}
            >
              <Input disabled style={readonlyFieldStyle} placeholder="Patient Name" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Policy Number"
              name="policyNumber"
              rules={[requiredRule("Please enter policy number")]}
            >
              <Input disabled style={readonlyFieldStyle} placeholder="Policy Number" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Card Number"
              name="cardNumber"
              rules={[requiredRule("Please enter card number")]}
            >
              <Input disabled style={readonlyFieldStyle} placeholder="Card Number" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Emp ID"
              name="empId"
              rules={[requiredRule("Please enter employee ID")]}
            >
              <Input disabled style={readonlyFieldStyle} placeholder="Emp ID" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Patient Mobile Number" name="patientMobile" rules={mobileTenDigitRules}>
              <Input
                placeholder="Mobile Number"
                maxLength={10}
                inputMode="numeric"
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                  form.setFieldValue("patientMobile", digits);
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="MRN of Patient"
              name="mrn"
              rules={[requiredRule("Please enter MRN")]}
            >
              <Input placeholder="MRN" />
            </Form.Item>
          </Col>
        </Row>

        <div style={sectionTitleStyle}>Admission Detail</div>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Date Of Admission"
              name="admissionDate"
              rules={[{ required: true, message: "Please select date of admission" }]}
            >
              <DatePicker
                showTime={{ format: "HH:mm:ss" }}
                format={HERITAGE_DATE_FORMAT}
                style={{ width: "100%" }}
                placeholder="Date Of Admission"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Probable Date Of Discharge"
              name="dischargeDate"
              rules={[requiredRule("Please select probable date of discharge")]}
            >
              <DatePicker
                showTime={{ format: "HH:mm:ss" }}
                format={HERITAGE_DATE_FORMAT}
                style={{ width: "100%" }}
                placeholder="Probable Date Of Discharge"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={6}>
            <Form.Item
              label="Ailment / Illness"
              name="diagnosisAilment"
              rules={[{ required: true, message: "Please enter ailment / illness" }]}
            >
              <Input placeholder="Ailment" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Doctor"
              name="treatingDoctorName"
              rules={[{ required: true, message: "Please enter doctor name" }]}
            >
              <Input placeholder="Doctor's Name" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Doctor's Registration Number"
              name="doctorRegistrationId"
              rules={[requiredRule("Please enter doctor registration number")]}
            >
              <Input placeholder="Doctor's Registration No." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Doctor's Mobile No."
              name="doctorMobileNo"
              rules={mobileTenDigitRules}
            >
              <Input
                placeholder="Doctor's Mobile No."
                maxLength={10}
                inputMode="numeric"
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                  form.setFieldValue("doctorMobileNo", digits);
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Admission Type"
              name="admissionType"
              rules={[{ required: true, message: "Please select admission type" }]}
            >
              <Select placeholder="--Select Admission Type--" allowClear>
                {HERITAGE_ADMISSION_TYPES.map((type) => (
                  <Select.Option key={type} value={type}>
                    {type}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div style={sectionTitleStyle}>Estimated Cost & Charges</div>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Estimated Cost"
              name="estimatedCost"
              rules={[
                { required: true, message: "Please enter estimated cost" },
                decimalAmountRule,
              ]}
            >
              <Input placeholder="Estimated Cost" inputMode="decimal" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Room/ICU Tariff"
              name="roomIcuTariff"
              rules={[requiredRule("Please select room category")]}
            >
              <Select placeholder="-- Select Room Category --">
                {HERITAGE_ROOM_CATEGORIES.map((cat) => (
                  <Select.Option key={cat} value={cat}>
                    {cat}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="No Of Days" name="noOfDays" rules={noOfDaysRules}>
              <Input placeholder="No Of Days" inputMode="numeric" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Room Rent" name="roomRent" rules={optionalDecimalRules}>
              <Input placeholder="Room Rent" inputMode="decimal" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Mou Discount" name="mouDiscount" rules={optionalDecimalRules}>
              <Input placeholder="Mou Discount" inputMode="decimal" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Discharge Request"
              name="dischargeRequest"
              valuePropName="checked"
            >
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="General Room Charge" name="generalRoomCharge" rules={optionalDecimalRules}>
              <Input placeholder="General Room Charge" inputMode="decimal" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="No. Of Days in General Room" name="generalRoomDays" rules={optionalWholeNumberRules}>
              <Input placeholder="No. Of Days in General Room" inputMode="numeric" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="ICU Charge" name="icuCharge" rules={optionalDecimalRules}>
              <Input placeholder="ICU Charge" inputMode="decimal" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="No Of Days in ICU" name="icuDays" rules={optionalWholeNumberRules}>
              <Input placeholder="No Of Days in ICU" inputMode="numeric" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Investigation Charge" name="investigationCharge" rules={optionalDecimalRules}>
              <Input placeholder="Investigation Charge" inputMode="decimal" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Doctor Charge" name="doctorCharge" rules={optionalDecimalRules}>
              <Input placeholder="Doctor Charge" inputMode="decimal" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Medicine Charge" name="medicineCharge" rules={optionalDecimalRules}>
              <Input placeholder="Medicine Charge" inputMode="decimal" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Package Rate" name="packageRate" rules={optionalDecimalRules}>
              <Input placeholder="Package Rate" inputMode="decimal" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Implant For PPN Procedure" name="implantForPpnProcedure" rules={optionalDecimalRules}>
              <Input placeholder="PPN Implant" inputMode="decimal" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Implant For Non PPN Procedure" name="implantForNonPpnProcedure" rules={optionalDecimalRules}>
              <Input placeholder="Other Implant" inputMode="decimal" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="No Of stent" name="noOfStent" rules={optionalWholeNumberRules}>
              <Input placeholder="No Of stent" inputMode="numeric" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Stent Cost" name="stentCost" rules={optionalDecimalRules}>
              <Input placeholder="Stent Cost" inputMode="decimal" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Other Charges" name="otherCharges" rules={optionalDecimalRules}>
              <Input placeholder="Other Charges" inputMode="decimal" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
          <Col xs={24} sm={12} md={12}>
            <div
              style={{
                background: "#fce4ec",
                borderRadius: 8,
                padding: "12px 16px",
                height: "100%",
              }}
            >
              <Form.Item label="Revert Reason" name="revertReason" style={{ marginBottom: 0 }}>
                <Input.TextArea rows={2} placeholder="Revert Reason" />
              </Form.Item>
            </div>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <div
              style={{
                background: "#e3f2fd",
                borderRadius: 8,
                padding: "12px 16px",
                height: "100%",
              }}
            >
              <Form.Item label="Hospital Reply" name="hospitalReply" style={{ marginBottom: 0 }}>
                <Input.TextArea rows={2} placeholder="Hospital Reply" />
              </Form.Item>
            </div>
          </Col>
        </Row>

        <h4 style={{ marginTop: 12 }}>Request Document Upload</h4>
        {renderDocumentRows("requestedDocuments")}

        <h4 style={{ marginTop: 12 }}>Response Document Upload</h4>
        {renderDocumentRows("responseDocuments")}

        <div
          style={{
            marginTop: 20,
            borderTop: "1px solid #D9D9D9",
            padding: "15px 20px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "30px",
          }}
        >
          {!isSubmitted ? (
            <>
              {hasExistingRecord && !isInitiated ? (
                <div style={{ width: "16%" }}>
                  <Button
                    type="primary"
                    onClick={() => handleSaveConfirmationModal("update")}
                    loading={isSaving}
                    disabled={isSubmitted}
                    style={actionButtonStyle}
                  >
                    UPDATE
                  </Button>
                </div>
              ) : (
                !hasExistingRecord && (
                  <div style={{ width: "16%" }}>
                    <Button
                      type="primary"
                      onClick={() => handleSaveConfirmationModal("save")}
                      loading={isSaving}
                      disabled={isSubmitted}
                      style={actionButtonStyle}
                    >
                      SAVE
                    </Button>
                  </div>
                )
              )}

              {hasFunctionality("casePreAuthIntimate") &&
                !isIntimated &&
                !isInitiated &&
                !isApproved && (
                  <div style={{ width: "16%" }}>
                    <Button
                      onClick={() => handleSaveConfirmationModal("intimate")}
                      loading={isIntimating}
                      disabled={isSubmitted}
                      style={actionButtonStyle}
                    >
                      Send To QC
                    </Button>
                  </div>
                )}

              {hasFunctionality("preAuthApprover") &&
                !isInitiated &&
                !isApproved &&
                !isSubmitted && (
                  <div style={{ width: "16%" }}>
                    <Button
                      onClick={() => handleSaveConfirmationModal("approve")}
                      loading={isApproving}
                      disabled={isSubmitted || isApproved}
                      style={actionButtonStyle}
                    >
                      QC Approve
                    </Button>
                  </div>
                )}

              {hasFunctionality("casePreAuthSubmission") && !isInitiated && !isSubmitted && (
                <div style={{ width: "16%" }}>
                  <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={isSubmitted}
                    style={actionButtonStyle}
                  >
                    {integratedWithPortal ? "Initiate" : "Submit Manually"}
                  </Button>
                </div>
              )}
            </>
          ) : null}
        </div>
      </Form>

      {isSubmitted && showVerifyButton && handleClaimValidation ? (
        <div
          style={{
            marginTop: 12,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={isValidationLoading}
            onClick={handleClaimValidation}
            style={{
              backgroundColor: "rgb(0, 76, 143)",
              color: "#fff",
              boxShadow: "0px 2px 8px 0px #00000040",
              minWidth: 140,
            }}
          >
            {registrationValidationStatus ? "View" : "Verify"}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(PreAuthHeritage);
